"use client";

import { useEffect, useEffectEvent, useRef } from "react";
import { Effect, Schedule } from "effect";
import { RpcClient } from "@effect/rpc";
import { useIdentity } from "@/lib/providers/identity";
import type { PlatformScope } from "@/domain/scope/platform-scope";
import { useCurrentPlatformScope, withRequestScope } from "@/shared/scope";
import { useCallStore } from "../model/call-store";
import { useChatSurfaceFlags } from "../model/use-chat-surface-flags";
import { ChatRpcError } from "@/contexts/chat/domain";
import { callEngine } from "../model/call-engine";
import { chatEq, ChatRpc } from "../model/api/chat-client";
import { IncomingCallModal } from "./incoming-call-modal";
import { CallStage } from "./call-stage";
import { MicIssueNotice } from "./mic-issue-notice";

/**
 * CallBridge — headless, mounted ONCE app-wide (next to ChatRealtimeBridge in
 * layout). Hosts the always-present remote-audio sink and renders the
 * incoming-call modal + the CallStage (panel ⇄ expanded popout).
 *
 * It no longer opens a socket: Chime carries the media and the ring arrives on
 * ChatRealtimeBridge's SSE stream, so this holds NO connection of its own — one
 * fewer always-on connection per tab than the 2026-07-16 incident had.
 *
 * SCOPE (FIX-SCOPE-STORE-3, D27). The engine's two handlers fire for a call
 * already in progress, so they run in the scope the CALL carries (call-store
 * `scope`, set where it was placed or rung); the route's scope is only the
 * fallback for a call that somehow carries none.
 */
export function CallBridge() {
  const {
    identity
  } = useIdentity();
  // Gated on chat_calls, NOT chat_messaging: a messaging-only org mounts no call
  // surface at all — no audio sink, no ring modal, no stage, no rejoin handlers.
  const isChatEnabled = useChatSurfaceFlags().callsEnabled;
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const routeScope = useCurrentPlatformScope();
  // Read when a handler fires, not reacted to: re-registering the handlers on
  // navigation would `leave()` the live call in the cleanup below.
  const callScope = useEffectEvent((): PlatformScope => useCallStore.getState().scope ?? routeScope);
  useEffect(() => {
    if (!identity || !isChatEnabled) return;
    callEngine.attachRemoteAudio(audioRef.current);
    // When our side ends the call INVOLUNTARILY (media stopped, or the peer
    // vanished), tell the server so it broadcasts call_ended to the other party —
    // the deterministic teardown that doesn't wait on Chime presence or a webhook
    // that can't reach localhost. Best-effort: a failed publish just falls back to
    // the slower Chime/webhook paths.
    callEngine.onInvoluntaryEnd = (callId, reason) => {
      const scope = callScope();
      void Effect.runPromise(Effect.gen(function* () {
        const rpc = yield* RpcClient.make(ChatRpc);
        // `reason` (Chime terminating status / peer-gone cause) is persisted as
        // end_reason so the drop is diagnosable after the fact.
        return yield* rpc.endCall({
          callId,
          status: "ended",
          reason
        });
      }).pipe(Effect.scoped, Effect.provide(chatEq.layer), withRequestScope(scope))).catch(() => undefined);
    };
    // Self-heal: when the live session dies under a call that's still on
    // (signaling socket closed unexpectedly), the engine asks for a fresh join
    // payload and rejoins the same room. null → the call is really over (or the
    // fetch kept failing) and the engine ends it with the original reason.
    callEngine.onNeedRejoin = callId_0 => {
      const scope_0 = callScope();
      return Effect.runPromise(Effect.gen(function* () {
        const rpc_0 = yield* RpcClient.make(ChatRpc);
        const res = yield* rpc_0.rejoinCall({
          callId: callId_0
        });
        return res.join as {
          meeting: unknown;
          attendee: unknown;
        };
      }).pipe(
      // This fetch runs exactly while the network is being flaky (the Chime
      // socket just died), so a transient transport/server failure must NOT
      // burn the one heal attempt — retry briefly WITH backoff (500ms → 1s)
      // so a struggling server isn't hammered. Authoritative answers never
      // retry: not_found / not_allowed (call is over or not ours) and
      // rate_limited (the server told us to ease off) end the heal — the
      // engine then ends the call with the original media-stop reason.
      Effect.retry({
        times: 2,
        schedule: Schedule.exponential("500 millis"),
        while: e => !(e instanceof ChatRpcError && (e.reason === "not_found" || e.reason === "not_allowed" || e.reason === "rate_limited"))
      }), Effect.scoped, Effect.provide(chatEq.layer), withRequestScope(scope_0))).catch(() => null);
    };
    return () => {
      callEngine.attachRemoteAudio(null);
      callEngine.onInvoluntaryEnd = null;
      callEngine.onNeedRejoin = null;
      void callEngine.leave();
    };
  }, [identity, isChatEnabled]);

  // The JSX is gated too, not only the effect: the stage reads the
  // conversations and teammates lists on mount, so a stage mounted with calls
  // off sent two chat RPCs per page load that the server's chat gate refused
  // (149 a day in production, 2026-10-25).
  if (!isChatEnabled) return null;
  return <>
      {/* Always mounted so ontrack has a sink before the remote stream arrives. */}
      <audio ref={audioRef} autoPlay hidden />
      <IncomingCallModal />
      <CallStage />
      {/* Outlives the CallStage on purpose: a blocked mic is fixed in browser
          settings, long after the failed call has torn itself down. */}
      <MicIssueNotice />
    </>;
}